import { Stack, Toolbar } from '@mui/material';
import Box from '@mui/material/Box';
import BottomActionButtons from '../Components/AppBars/BottomBar/BottomActionsButtons';
import BottomControlBar from '../Components/AppBars/BottomBar/BottomControlBar';
import DeployButton from '../Components/AppBars/BottomBar/DeployButton';

import { closestCenter, DndContext, DragEndEvent, DragStartEvent, DragMoveEvent, DragOverlay, type CollisionDetection } from '@dnd-kit/core';
import { snapCenterToCursor } from '@dnd-kit/modifiers';
import { GridStackOptions, GridStackWidget } from 'gridstack';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import Dashboard from '../Components/Dashboard/Dashboard';
import { createLiveGraphStore, LiveGraphStoreProvider } from '../Components/Dashboard/LiveGraphStore';
import { useDashboardCapabilities } from '../Components/Dashboard/dashboardCapabilities';
import type { WidgetOptions } from '../Components/Dashboard/dashboardTypes';
import DashboardToolbar from '../Components/Dashboard/DashboardToolbar';
import { CustomCallableStreamsProvider } from '../Components/Dashboard/CustomCallableStreamsContext';
import { GridStackProvider } from '../Components/Dashboard/GridStack';
import { useAppDispatch, useAppSelector } from '../redux/ReduxHooks';
import { parseHardwareFromProjectCode } from '../Components/CodeEditor/ConfigurationTabs/hardwareParser';
import { applicationSlice } from '../redux/ApplicationSlice';
import { useLogging } from '../logging/logging';
import { buildDriveUnitPortOccupanciesFromHardwareCards } from '../utils/buildDriveUnitPortOccupanciesFromHardwareCards';
import { LAYOUT_CONSTANTS } from '../utils/layout-constants';
import {
  isPythonTreeSitterReady,
  prewarmPythonTreeSitter,
  requestPythonTreeSitterBindingsHydrate,
  subscribePythonTreeSitterReadyTransition,
} from '../utils/pythonTreeSitterInputBindings';
import DashboardDrawer from '../Components/Dashboard/DashboardDrawer';
import { useLeftDrawerLayout } from '../hooks/ui/useLeftDrawerLayout';
import { resolveDragWidgetMinSize, resolveWidgetSizeConstraints } from '../utils/dashboardWidgetUtils';
import { createDashboardWidgetContent, canAddWidget, getStreamKeysForWidget, MAX_SENSOR_STREAMS } from '../utils/sensor-stream-tracker';
import StreamLimitDialog from '../Components/Dialogs/Dashboard/StreamLimitDialog';
import { useSyncDataStreamKeysFromLayout } from '../hooks/dashboard/useSyncDataStreamKeysFromLayout';
import {
  clampMotorMultiMetrics,
  defaultMotorMultiMetrics,
  getMultiAxisDataPickerEntries,
  motorMultiStreamKeysForMetrics,
  motorStreamKeysForPorts,
  motorStreamKeysForSelectedPorts,
  parseMotorStreamSensorKey,
  resolveElectronicSensorWidgetDrop,
  resolveOtherElectronicsWidgetDrop,
} from '../utils/electronicsDashboardUtils';
import {
  BOOLEAN_WIDGET_TYPE,
  MECHANISM_WIDGET_TYPE,
  MOTOR_MULTI_WIDGET_TYPE,
  MOTOR_WIDGET_TYPE,
  OTHER_WIDGET_TYPE,
  SENSOR_WIDGET_TYPE,
  isElectronicSensorStreamSensorKey,
  isMotorStreamSensorKey,
  parseMotorMultiWidgetDataKey,
} from '../Components/Graphs/sensor-utils';
import { DashboardCanvasShell } from '../Components/Dashboard/DashboardCanvasShell';
import {
  checkCollision as checkCollisionInLayout,
  findNearestPlacement as findNearestPlacementInGrid,
  isWithinStickyRect,
} from './dashboardPlacement';

// Cell size is constant across all breakpoints
const CELL_SIZE = 128;
const DROP_STICKY_PX = 24;
const NEAREST_PLACEMENT_SEARCH_RADIUS_CELLS = 4;

// Responsive breakpoints configuration - determines grid size on INITIAL LOAD only
// Once the grid is created, it stays fixed so widgets don't move when window resizes
// Users scroll to see content if window becomes smaller than canvas
// Breakpoints are checked in order - first match wins (largest to smallest)
interface GridBreakpoint {
  minWidth: number; // Minimum window width for this breakpoint
  columns: number; // Number of columns at this breakpoint
  minRows: number; // Minimum visible rows at this breakpoint
}

const GRID_BREAKPOINTS: GridBreakpoint[] = [
  { minWidth: 1920, columns: 18, minRows: 12 }, // Large/4K monitors (1920px+) → 2304px canvas
  { minWidth: 1440, columns: 13, minRows: 8 }, // Desktop/16" laptops (1440-1919px) → 1664px canvas
  { minWidth: 1280, columns: 10, minRows: 7 }, // Small desktop/laptop (1280-1439px) → 1280px canvas
  { minWidth: 0, columns: 8, minRows: 6 }, // Smaller screens (<1280px) → 1024px canvas
];

/**
 * Get the appropriate grid layout for the current window width
 * Called ONCE on initial load - grid stays fixed after that
 */
function getLayoutForWidth(width: number): GridBreakpoint {
  return GRID_BREAKPOINTS.find((bp) => width >= bp.minWidth) || GRID_BREAKPOINTS[GRID_BREAKPOINTS.length - 1];
}

/**
 * Create GridStack options for the given layout
 */
function createGridOptions(layout: GridBreakpoint): GridStackOptions {
  return {
    acceptWidgets: true,
    removable: false,
    margin: 0, // Keep 0 so canvas size and saved layouts stay correct; spacing via CSS on content
    cellHeight: CELL_SIZE, // Fixed 128px cells
    cellHeightUnit: 'px', // Use pixels, not percentage
    draggable: {
      handle: '.widget-header', // Only allow dragging from widget header
    },
    resizable: {
      handles: 'all', // Allow resizing from all sides
    },
    float: true, // Allow widgets to stay at any Y position without compacting
    column: layout.columns, // Dynamic column count based on screen size
    rtl: false, // Ensure left-to-right layout
    minRow: layout.minRows, // Dynamic minimum rows based on screen size
  };
}

const DashboardPage = () => {
  const dispatch = useAppDispatch();
  const logger = useLogging();
  const { t } = useTranslation();
  const [activeID, setActiveId] = useState<string | null>(null);
  const [displayWidth, setDisplayWidth] = useState(0);
  const [displayHeight, setDisplayHeight] = useState(0);
  const [display, setDisplay] = useState<ReactNode | null>(null);
  const [isValidDrop, setIsValidDrop] = useState<boolean>(true);
  const [showGridLines, setShowGridLines] = useState(false);
  const [streamLimitDialogOpen, setStreamLimitDialogOpen] = useState(false);
  const [streamLimitMessage, setStreamLimitMessage] = useState('');
  const [liveGraphStore] = useState(createLiveGraphStore);
  const { leftDrawer, leftDrawerContentExpanded, leftDrawerWidth, leftDrawerContentWidth } = useLeftDrawerLayout('dashboard');
  const project = useAppSelector((state) => state.project.loadedProject);
  const { canEditDashboardLayout } = useDashboardCapabilities();
  const projectCode = project?.program?.code ?? '';
  const driveUnitPorts = useAppSelector((state) => state.application.driveUnitPorts);
  const hardwareParseRunIdRef = useRef(0);
  // Bumped when Tree-sitter transitions to ready so hardware parse re-runs after a late init.
  const [treeSitterReadyEpoch, setTreeSitterReadyEpoch] = useState(0);

  useSyncDataStreamKeysFromLayout();

  // Warm Tree-sitter while on the dashboard so Code Editor Control mutations are
  // less likely to race WASM init after navigation. Init is the readiness probe
  // (prewarm retries with backoff; hardware parse also re-attempts). On a not-ready →
  // ready transition, request hydrate so Code Editor listeners reparse when mounted;
  // this page also re-runs drive-unit occupancy parse via the epoch subscription.
  useEffect(() => {
    return subscribePythonTreeSitterReadyTransition(() => {
      setTreeSitterReadyEpoch((epoch) => epoch + 1);
    });
  }, []);

  useEffect(() => {
    let cancelled = false;
    const wasReady = isPythonTreeSitterReady();
    void prewarmPythonTreeSitter().then((ok) => {
      if (cancelled) return;
      if (!ok) {
        logger.error('Tree-sitter WASM prewarm failed');
        return;
      }
      if (!wasReady) {
        requestPythonTreeSitterBindingsHydrate();
      }
    });
    return () => {
      cancelled = true;
    };
  }, [logger]);

  useEffect(() => {
    hardwareParseRunIdRef.current += 1;

    if (!projectCode) {
      dispatch(applicationSlice.actions.clearNonSyntheticDriveUnitPorts());
      return;
    }

    const runId = hardwareParseRunIdRef.current;
    let cancelled = false;
    const handle = setTimeout(async () => {
      try {
        const parsed = await parseHardwareFromProjectCode({
          code: projectCode,
          existingInputBindings: [],
          logger,
        });
        if (cancelled || runId !== hardwareParseRunIdRef.current) return;

        dispatch(applicationSlice.actions.clearNonSyntheticDriveUnitPorts());
        const driveUnitRows = buildDriveUnitPortOccupanciesFromHardwareCards(parsed.dynamicCards, undefined, projectCode);
        for (const row of driveUnitRows) {
          dispatch(applicationSlice.actions.onUpdateDriveUnitPorts(row));
        }
      } catch (err) {
        logger.error('Error parsing project hardware on dashboard', err);
      }
    }, 50);

    return () => {
      cancelled = true;
      clearTimeout(handle);
      // Invalidate in-flight parse so a completion after unmount/dep-change cannot dispatch.
      hardwareParseRunIdRef.current += 1;
    };
  }, [projectCode, dispatch, logger, treeSitterReadyEpoch]);

  // Grid layout is determined ONCE on initial load based on window size
  // After that, it stays fixed - widgets don't move when window resizes
  const [initialLayout] = useState<GridBreakpoint>(() => getLayoutForWidth(window.innerWidth));

  // Grid options are fixed after initial load
  const gridOptions = useMemo(() => createGridOptions(initialLayout), [initialLayout]);

  // Canvas dimensions are fixed after initial load
  const canvasWidth = initialLayout.columns * CELL_SIZE;

  // Memoize dashboard layout to avoid changing on every render
  const dashboardLayout = useMemo(() => project?.dashboardLayout || [], [project?.dashboardLayout]);

  // Drawer toggling is controlled by clicking tabs in the drawer; no local handler needed here.

  const dashboardRef = useRef<{
    addWidget: (fn: (id: string) => Omit<GridStackWidget, 'id'>) => void;
    getColumnCount?: () => number;
    getCellHeight?: () => number;
    getGridStack?: () => unknown;
  } | null>(null);

  const checkCollision = useCallback(
    (x: number, y: number, w: number, h: number) => checkCollisionInLayout(dashboardLayout, x, y, w, h),
    [dashboardLayout]
  );

  const getDropTopLeft = useCallback(
    (event: { active: { rect: { current: { translated?: { left: number; top: number } | ClientRect | null } } } }) => {
      const translated = event.active.rect.current.translated ?? null;
      const dropX = translated ? translated.left : undefined;
      const dropY = translated ? translated.top : undefined;
      if (dropX === undefined || dropY === undefined) return null;
      return { dropX, dropY };
    },
    []
  );

  const getDesiredGridPos = useCallback((gridRect: { left: number; top: number }, drop: { dropX: number; dropY: number }, h: number) => {
    // Align by widget visual center (the overlay is centered to cursor)
    const calcY = drop.dropY - (h * CELL_SIZE - 20) / 2;
    const rawX = (drop.dropX - gridRect.left) / CELL_SIZE;
    const rawY = (calcY - gridRect.top) / CELL_SIZE;
    return { rawX, rawY };
  }, []);

  const findNearestPlacement = useCallback(
    (desired: { x: number; y: number }, w: number, h: number, columns: number) => {
      return findNearestPlacementInGrid({
        desiredX: desired.x,
        desiredY: desired.y,
        w,
        h,
        columns,
        searchRadiusCells: NEAREST_PLACEMENT_SEARCH_RADIUS_CELLS,
        collides: checkCollision,
      });
    },
    [checkCollision]
  );

  const dashboardCollisionDetection = useMemo((): CollisionDetection => {
    return (args) => {
      const { droppableContainers, pointerCoordinates } = args;
      if (!pointerCoordinates) return closestCenter(args);

      const grid = droppableContainers.find((c) => c.id === 'grid-stack');
      if (grid) {
        const rect = grid.rect.current;
        if (isWithinStickyRect(pointerCoordinates, rect, DROP_STICKY_PX)) {
          return [{ id: grid.id, data: { droppableContainer: grid }, rect }];
        }
      }

      return closestCenter(args);
    };
  }, []);

  const handleDrop = useCallback(
    (event: DragEndEvent) => {
      setShowGridLines(false);
      if (!canEditDashboardLayout) return;
      if (!event.over || event.over.id !== 'grid-stack') return;
      const type = event.active.data.current?.type;
      const dataKey = event.active.data.current?.dataKey;
      const widgetStyle = event.active.data.current?.style as string | undefined;
      const widgetOptions = event.active.data.current?.options as WidgetOptions | undefined;
      if (!type) return;

      const dataKeyStr = String(dataKey ?? '');
      const { minW, minH, maxW, maxH } = resolveWidgetSizeConstraints(
        type,
        dataKeyStr,
        Number(event.active.data.current?.minW) || undefined,
        Number(event.active.data.current?.minH) || undefined
      );
      let sensorKeys = getStreamKeysForWidget(type, dataKeyStr);
      let dropOptions: WidgetOptions | undefined = widgetOptions;

      if (type === MOTOR_MULTI_WIDGET_TYPE && dataKeyStr) {
        const parsed = parseMotorMultiWidgetDataKey(dataKeyStr);
        if (parsed) {
          const availableMetrics = getMultiAxisDataPickerEntries(parsed.port, parsed.prefix, driveUnitPorts, projectCode).map(
            (entry) => entry.metric
          );
          const fromDrag = widgetOptions?.selectedMotorMetrics;
          const defaultMetrics = fromDrag?.length
            ? clampMotorMultiMetrics(fromDrag, availableMetrics)
            : defaultMotorMultiMetrics(availableMetrics, parsed.prefix);
          dropOptions = {
            ...dropOptions,
            selectedMotorPort: parsed.port,
            selectedMotorMetrics: defaultMetrics,
          };
          sensorKeys = motorMultiStreamKeysForMetrics(dataKeyStr, parsed.port, defaultMetrics, {
            portRows: driveUnitPorts,
            projectCode,
          });
        }
      }

      if (
        (type === MOTOR_WIDGET_TYPE || type === MECHANISM_WIDGET_TYPE || type === BOOLEAN_WIDGET_TYPE) &&
        dataKeyStr &&
        isMotorStreamSensorKey(dataKeyStr)
      ) {
        const parsed = parseMotorStreamSensorKey(dataKeyStr);
        if (parsed) {
          const portsFromOptions = widgetOptions?.selectedMotorPorts?.filter((port): port is number => Number.isFinite(port));
          const selectedPorts = portsFromOptions?.length ? [...new Set(portsFromOptions)].sort((a, b) => a - b) : [parsed.port];
          dropOptions = { ...dropOptions, selectedMotorPorts: selectedPorts };
          const streamKind = type === MECHANISM_WIDGET_TYPE ? 'mechanism' : 'motor';
          const keysForPorts = motorStreamKeysForSelectedPorts(driveUnitPorts, projectCode, parsed.metric, selectedPorts, streamKind);
          sensorKeys = keysForPorts.length ? keysForPorts : motorStreamKeysForPorts(dataKeyStr, selectedPorts);
        }
      }

      if ((type === SENSOR_WIDGET_TYPE || (type === BOOLEAN_WIDGET_TYPE && isElectronicSensorStreamSensorKey(dataKeyStr))) && dataKeyStr) {
        const resolved = resolveElectronicSensorWidgetDrop(dataKeyStr, widgetOptions);
        if (resolved) {
          dropOptions = { ...dropOptions, ...resolved.dropOptions };
          sensorKeys = resolved.sensorKeys;
        }
      }

      if (type === OTHER_WIDGET_TYPE && dataKeyStr) {
        const resolved = resolveOtherElectronicsWidgetDrop(dataKeyStr, widgetOptions);
        if (resolved) {
          dropOptions = { ...dropOptions, ...resolved.dropOptions };
          sensorKeys = resolved.sensorKeys;
        }
      }

      // Check stream limit before proceeding
      const streamCheck = canAddWidget(dashboardLayout, sensorKeys);
      if (!streamCheck.canAdd) {
        setStreamLimitMessage(
          t('dashboard.streamLimit.warning.message', {
            defaultValue: 'You have reached the maximum number of sensor stream items ({{max}}). Please remove some widgets before adding more.',
            max: MAX_SENSOR_STREAMS,
          })
        );
        setStreamLimitDialogOpen(true);
        setIsValidDrop(true); // Reset visual feedback
        return;
      }

      const gridRect = event.over.rect;

      const drop = getDropTopLeft(event);
      if (!drop) return;

      const { rawX, rawY } = getDesiredGridPos(gridRect, drop, minH);
      const desired = { x: rawX, y: rawY };
      const placement = findNearestPlacement(desired, minW, minH, initialLayout.columns);
      if (!placement) return;

      dashboardRef.current?.addWidget((id) => ({
        w: minW,
        h: minH,
        x: placement.x,
        y: placement.y,
        minH,
        minW,
        maxH,
        maxW,
        id: id,
        content: createDashboardWidgetContent({
          name: type,
          dataKey: String(dataKey ?? ''),
          style: widgetStyle,
          options: dropOptions,
          props: { content: id },
          sensorKeys,
          streamProjectContext: { portRows: driveUnitPorts, projectCode },
        }),
      }));

      // Reset visual feedback
      setIsValidDrop(true);
    },
    [
      canEditDashboardLayout,
      dashboardRef,
      dashboardLayout,
      findNearestPlacement,
      getDesiredGridPos,
      getDropTopLeft,
      initialLayout.columns,
      driveUnitPorts,
      projectCode,
      t,
    ]
  );

  const handleDrag = useCallback(
    (event: DragStartEvent) => {
      if (!canEditDashboardLayout) return;
      setShowGridLines(true);
      setActiveId(event.active.id.toString());

      const { minW, minH } = resolveDragWidgetMinSize(
        event.active.data.current?.type,
        event.active.data.current?.minW,
        event.active.data.current?.minH
      );

      if (!dashboardRef.current?.getCellHeight || !dashboardRef.current?.getColumnCount) return;

      setDisplayWidth(dashboardRef.current.getCellHeight() * minW);
      setDisplayHeight(dashboardRef.current.getCellHeight() * minH);
      setDisplay(event.active.data.current?.display);
    },
    [canEditDashboardLayout, dashboardRef]
  );

  const handleDragging = useCallback(
    (event: DragMoveEvent) => {
      if (!canEditDashboardLayout) return;

      const type = event.active.data.current?.type;
      const dataKey = event.active.data.current?.dataKey;
      const { minW, minH } = resolveDragWidgetMinSize(type, event.active.data.current?.minW, event.active.data.current?.minH);

      if (!event.over || event.over.id !== 'grid-stack') {
        setIsValidDrop(true);
        return;
      }

      const drop = getDropTopLeft(event);
      if (!drop) return;

      const sensorKeys = type ? getStreamKeysForWidget(type, String(dataKey ?? '')) : [];
      const withinStreamLimit = sensorKeys.length === 0 || canAddWidget(dashboardLayout, sensorKeys).canAdd;

      const { rawX, rawY } = getDesiredGridPos(event.over.rect, drop, minH);
      const placement = findNearestPlacement({ x: rawX, y: rawY }, minW, minH, initialLayout.columns);
      setIsValidDrop(Boolean(placement && withinStreamLimit));
    },
    [canEditDashboardLayout, dashboardLayout, findNearestPlacement, getDesiredGridPos, getDropTopLeft, initialLayout.columns]
  );

  // Memoize portalRoot to avoid checking on every render
  const portalRoot = useMemo(() => document.getElementById('root'), []);

  return (
    <Box height="100vh" sx={{ display: 'flex' }}>
      <DndContext collisionDetection={dashboardCollisionDetection} onDragEnd={handleDrop} onDragMove={handleDragging} onDragStart={handleDrag}>
        <DashboardDrawer openWidth={leftDrawerContentWidth} isOpen={leftDrawer} />

        {/* Main Content */}
        <Stack
          component="main"
          py={0}
          px={0}
          sx={{
            flexGrow: 1,
            minWidth: 0,
            bgcolor: 'background.editor',
            overflow: 'hidden',
            height: `calc(100vh - ${LAYOUT_CONSTANTS.BOTTOM_CONTROL_BAR_HEIGHT}px)`,
          }}
        >
          <Toolbar />
          <CustomCallableStreamsProvider>
            <LiveGraphStoreProvider store={liveGraphStore}>
              <DashboardToolbar />
              <DashboardCanvasShell canvasWidth={canvasWidth}>
                <GridStackProvider initialOptions={gridOptions}>
                  <Dashboard
                    ref={dashboardRef}
                    drawerOpen={leftDrawer}
                    liveGraphStore={liveGraphStore}
                    canvasWidth={canvasWidth}
                    showGridLines={showGridLines}
                  />
                </GridStackProvider>
              </DashboardCanvasShell>
            </LiveGraphStoreProvider>
          </CustomCallableStreamsProvider>
        </Stack>
        {portalRoot &&
          createPortal(
            <DragOverlay dropAnimation={null} zIndex={1500} modifiers={[snapCenterToCursor]}>
              {activeID ? (
                <Box
                  width={displayWidth}
                  height={displayHeight}
                  sx={{
                    backgroundColor: 'background.paper',
                    color: 'text.primary',
                    cursor: 'grabbing',
                    borderRadius: '1rem',
                    boxShadow: '0px 4px 12px rgba(0,0,0,0.3)',
                    pointerEvents: 'none',
                    border: `3px solid ${isValidDrop ? 'rgba(76, 175, 80, 0.5)' : 'rgba(244, 67, 54, 0.5)'}`,
                    transition: 'border-color 0.2s ease',
                  }}
                >
                  {display}
                </Box>
              ) : null}
            </DragOverlay>,
            portalRoot
          )}
      </DndContext>

      {/* Bottom Control Bar */}
      <BottomControlBar
        centerContent={<BottomActionButtons />}
        rightContent={<DeployButton />}
        leftDrawerOpen={leftDrawerContentExpanded}
        leftDrawerWidth={leftDrawerWidth}
        rightDrawerOpen={false}
      />

      {/* Stream Limit Warning Dialog */}
      <StreamLimitDialog open={streamLimitDialogOpen} message={streamLimitMessage} onClose={() => setStreamLimitDialogOpen(false)} />
    </Box>
  );
};

export default DashboardPage;
